import { message } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  listOperators,
  listOperatorsIncludingArchived,
} from "@/modules/integrations/accounts/operators";
import type { Operator } from "@/providers/smarta-accounts/accounts-api-client";
import { reportError } from "@/v1/sentry";
import {
  clearLeadAssignee,
  LEADS_PAGE_SIZE,
  listLeads,
  setLeadAssignee,
} from "./api";
import {
  countLeadDocuments,
  deleteLeadDocument,
  getLeadDocumentUrl,
  listLeadDocuments,
  uploadLeadDocument,
} from "./documents/api";
import { LeadDocuments } from "./documents/LeadDocuments";
import { NewLeadModal } from "./NewLeadModal";
import { OnboardingView } from "./OnboardingView";
import { ONBOARDING_PERMISSION_SCOPES } from "./permissions";
import { Lead } from "./types";

/*
 * Only operators who can open this view may own a lead: an ACCOUNTING or
 * READONLY operator gets a 403 from the lead routes. A lead already assigned
 * to someone outside this list still shows that name (see AssigneeSelect).
 */
const canOwnLeads = (operator: Operator) =>
  ONBOARDING_PERMISSION_SCOPES.some(
    (scope) => scope === operator.permissionScope
  );

export const OnboardingPage = () => {
  const { t } = useTranslation("Onboarding");
  const [leads, setLeads] = useState<Lead[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadVersion, setReloadVersion] = useState(0);
  const [newLeadOpen, setNewLeadOpen] = useState(false);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [operatorsError, setOperatorsError] = useState(false);
  const [savingLeadIds, setSavingLeadIds] = useState<ReadonlySet<string>>(
    new Set()
  );
  const [fileCounts, setFileCounts] = useState<
    ReadonlyMap<string, number | null>
  >(new Map());
  /*
   * Paging quickly can resolve requests out of order. Only the latest request
   * may write the table, so an older page never overwrites a newer one.
   */
  const latestRequest = useRef(0);
  /*
   * The same goes for each lead's file count: an upload or a delete refreshes
   * it while the page's first count may still be on its way, so only a lead's
   * latest count request may write its cell.
   */
  const latestCountRequests = useRef(new Map<string, number>());

  const setFileCount = (leadId: string, count: number | null) =>
    setFileCounts((current) => new Map(current).set(leadId, count));

  /*
   * smarta-documents has no count across leads, so each visible row asks for
   * its own total (one row per request). Each cell fills in as its answer
   * arrives; a failure leaves that one cell at a dash.
   */
  const loadFileCount = useCallback(
    async (leadId: string, isCurrent: () => boolean = () => true) => {
      const requests = latestCountRequests.current;
      const request = (requests.get(leadId) ?? 0) + 1;
      requests.set(leadId, request);
      let count: number | null;
      try {
        count = await countLeadDocuments(leadId);
      } catch {
        count = null;
      }
      if (isCurrent() && requests.get(leadId) === request) {
        setFileCount(leadId, count);
      }
    },
    []
  );

  const loadPage = useCallback(async () => {
    const request = ++latestRequest.current;
    setLoading(true);
    setError(false);
    try {
      const result = await listLeads(page);
      if (latestRequest.current !== request) return;
      setLeads(result.leads);
      setTotal(result.total);
      setFileCounts(new Map());
      for (const { publicId } of result.leads) {
        void loadFileCount(publicId, () => latestRequest.current === request);
      }
    } catch {
      if (latestRequest.current === request) setError(true);
    } finally {
      if (latestRequest.current === request) setLoading(false);
    }
  }, [page, loadFileCount]);

  useEffect(() => {
    void loadPage();
    return () => {
      // Drop the answer of a request the page no longer waits for.
      latestRequest.current += 1;
    };
  }, [loadPage, reloadVersion]);

  /*
   * One operator list per page load, filtered once to those who can own a
   * lead, and shared by every row's assignee select and the New lead form.
   */
  useEffect(() => {
    let active = true;
    listOperators()
      .then((result) => active && setOperators(result.filter(canOwnLeads)))
      .catch(() => active && setOperatorsError(true));
    return () => {
      active = false;
    };
  }, []);

  /*
   * Operator id to name, for the uploader of each lead's files. Archived
   * operators are included on purpose: a file outlives the person who
   * uploaded it. Fetched on the first expanded row, then shared by every row;
   * a failure is reported, names nobody, and lets the next row try again.
   */
  const operatorNames = useRef<Promise<ReadonlyMap<string, string>>>();
  const loadOperatorNames = useCallback(() => {
    if (!operatorNames.current) {
      operatorNames.current = listOperatorsIncludingArchived().then(
        (all) =>
          new Map(all.map((operator) => [operator.id, operator.displayName])),
        (error) => {
          operatorNames.current = undefined;
          reportError(error as Error);
          return new Map<string, string>();
        }
      );
    }
    return operatorNames.current;
  }, []);

  const reload = () => setReloadVersion((current) => current + 1);

  const markSaving = (leadId: string, saving: boolean) =>
    setSavingLeadIds((current) => {
      const next = new Set(current);
      if (saving) next.add(leadId);
      else next.delete(leadId);
      return next;
    });

  /*
   * The row is replaced with the API's answer. On failure nothing changes, so
   * the select, which reads the lead, falls back to the previous assignee.
   */
  const changeAssignee = async (lead: Lead, operatorId: string | undefined) => {
    markSaving(lead.publicId, true);
    try {
      const updated = operatorId
        ? await setLeadAssignee(lead.publicId, operatorId)
        : await clearLeadAssignee(lead.publicId);
      setLeads((current) =>
        current.map((row) =>
          row.publicId === updated.publicId ? updated : row
        )
      );
    } catch {
      message.error(t("assignee.error"));
    } finally {
      markSaving(lead.publicId, false);
    }
  };

  return (
    <>
      <OnboardingView
        leads={leads}
        total={total}
        page={page}
        pageSize={LEADS_PAGE_SIZE}
        loading={loading}
        error={error}
        onPageChange={setPage}
        onRetry={reload}
        onNewLead={() => setNewLeadOpen(true)}
        operators={operators}
        savingLeadIds={savingLeadIds}
        onAssigneeChange={(lead, operatorId) =>
          void changeAssignee(lead, operatorId)
        }
        fileCounts={fileCounts}
        renderExpandedLead={(lead) => (
          <LeadDocuments
            leadId={lead.publicId}
            loadDocuments={listLeadDocuments}
            uploadDocument={uploadLeadDocument}
            deleteDocument={deleteLeadDocument}
            getFileUrl={getLeadDocumentUrl}
            loadOperatorNames={loadOperatorNames}
            onUploaded={(leadId) => void loadFileCount(leadId)}
            onDeleted={(leadId) => void loadFileCount(leadId)}
          />
        )}
      />
      <NewLeadModal
        open={newLeadOpen}
        operators={operators}
        operatorsError={operatorsError}
        onClose={() => setNewLeadOpen(false)}
        onCreated={() => {
          setNewLeadOpen(false);
          /*
           * GET /api/leads lists newest first, so the new lead is on page 1.
           * From a later page, changing the page is what refetches; on page 1
           * only a reload does. Never both: this runs after an awaited POST,
           * where the legacy root does not batch, so both would fetch twice.
           */
          if (page === 1) reload();
          else setPage(1);
        }}
      />
    </>
  );
};
